<template lang="pug">
section.section(
  :class="{ 'section--unlocked': unlocked, 'section--active': active, 'section--after': after || active, 'section--last': last && active, 'section--contains-sticky': containsStickyScreen(id) }"
  :id="id"
  :uc-unlocked="unlocked"
  tabindex="-1"
)
  //- FIXME disable unmounting for nested sections for now
  //- FIXME need this hack for quiz template, may harm performance
  //- template(v-if="wordDoc || unlocked")
  slot
</template>

<script lang="ts">
import { gsap } from 'gsap';
import { defineComponent, nextTick, computed, watch } from 'vue';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  getCompleted,
  getViewed,
  set,
  setCompleted,
  setViewed
} from '../lib/SCORM';
import {
  currentAudioOwner,
  playNextInQueue,
  stop as stopAudio
} from '../lib/audio';
import { mapActions } from 'pinia';
import { play, queue } from '../lib/audio';
import useQuizzes from '../stores/useQuizzes';
import { debounce } from 'lodash-es';
import useSuspend from '../lib/useSuspend';
interface ExtendedElement extends HTMLElement {
  play?(): void;
  reset?(): void;
}

// one set of triggers for reduced motion (when the screen-no-fade class is applied) but we still need to directly apply the '-in' class to the elements without the timeline delays
/** Suspend-data: last-page once per runtime when a pass-course section is entered. */
let lastPageSuspendDataSet = false;

const fadeTriggersSelector =
  '.screen .trigger, .screen .fade-in, .screen .enter-grow, .screen .slide-up, .screen .slide-from-right, .screen .slide-from-left, .screen .uc-animate, .screen .uc-lottie[data-autoplay]';

const noFadeTriggersSelector =
  '.screen .trigger, .screen .fade-in:not(.screen--no-fade *), .screen .enter-grow, .screen .slide-up, .screen .slide-from-right, .screen .slide-from-left, .screen .uc-animate:not(.screen--no-fade *), .screen .uc-lottie[data-autoplay]';

// screen images but not bg images unless they have the animate-bg class
const assetTriggersSelector =
  '.screen img:not(.screen__fill img:not(.animate-bg))';

gsap.registerPlugin(ScrollTrigger);

export default defineComponent({
  inject: [
    'currentSection',
    'sections',
    'unlockedSections',
    'page',
    'wordDoc',
    'isEditModeActive'
  ],

  provide() {
    return {
      sectionId: this.id,
      sectionActive: computed(() => this.currentSection === this.$el),
      /** Keep video tech mounted for this section, active, and immediate neighbours (smoother scroll). */
      sectionVideoKeepAlive: computed(() => {
        const sections = this.sections as HTMLElement[] | undefined;
        const current = this.currentSection as HTMLElement | null | undefined;
        const el = this.$el as HTMLElement;
        if (!sections?.length || !current || !el) return true;
        const i = sections.indexOf(el);
        const c = sections.indexOf(current);
        if (i < 0 || c < 0) return true;
        return Math.abs(i - c) <= 1;
      })
    };
  },

  props: { id: { type: String, required: true } },

  setup() {
    const suspend = useSuspend();
    return { suspend };
  },

  data() {
    return {
      st1: null,
      st2: null
    };
  },

  computed: {
    unlocked() {
      return this.unlockedSections.includes('|' + this.id + '|');
    },
    active() {
      return this.currentSection === this.$el;
    },
    after() {
      const { currentSection, sections } = this;

      if (!sections) {
        return;
      }

      const currentIndex = sections.indexOf(currentSection);
      const thisIndex = sections.indexOf(this.$el);

      return thisIndex < currentIndex;
    },
    last() {
      const { currentSection, sections } = this;

      if (!sections) {
        return;
      }

      const currentIndex = sections.indexOf(currentSection);
      const sectionsLength = sections.length - 1;

      return currentIndex === sectionsLength;
    }
  },

  mounted() {
    const el: HTMLElement = this.$el;
    const page = el.closest('.page');

    if (!page) return;

    const scroller = this.$el.closest('.scroll-page__content');

    const completeCourse = this.completeCourse;

    if (window.matchMedia('(min-width: 800px)').matches) {
      el.querySelectorAll('[uc-parallax]').forEach(trigger => {
        const y = 100 * +trigger.getAttribute('uc-parallax');

        if (!y) {
          return;
        }

        gsap.fromTo(
          trigger,
          { y },
          {
            y: -y,
            ease: 'none',
            scrollTrigger: {
              trigger,
              scroller,
              start: () => `top 100%+=${y}px`,
              end: () => `bottom 0%+=${y}px`,
              scrub: true
            }
          }
        );
      });
    }

    // if the element is taller than 60% of the viewport height, we need to change the start and end points
    // to avoid the section being too short and the scroll trigger firing too early
    let isTall = el.offsetHeight > window.innerHeight * 0.6;

    ScrollTrigger.addEventListener('refreshInit', () => {
      isTall = el.offsetHeight > window.innerHeight * 0.6;
    });

    // scrolltrigger to track the scroll position of this section and fire events when we scroll in and out of view
    this.st1 = ScrollTrigger.create({
      // markers: true,
      trigger: el,
      scroller,
      start: () => (isTall ? 'top 55%' : 'top 90%'),
      end: () => (isTall ? 'bottom 45%' : 'bottom 10%'),
      onUpdate: toggleParams => {
        el.dispatchEvent(
          new CustomEvent('scrollsectionscrolled', {
            bubbles: true,
            detail: {
              st: this.st1,
              id: this.id,
              progress: toggleParams.progress.toFixed(3),
              direction: toggleParams.direction
            }
          })
        );
      },
      onToggle: toggleParams => {
        const { isActive } = toggleParams;
        const { id } = this;

        if (isActive && el.offsetHeight > 0) {
          if (document.documentElement.contains(document.activeElement)) {
            el.focus({ preventScroll: true });
          }

          const viewed = getViewed(id);
          if (!viewed) setViewed(id);

          const eventParams = {
            bubbles: true,
            detail: this.st1
          };

          const eventNames = [
            'scrollsectionentered',
            'scrollsectionentered--' + id,
            'scrollsectionentered--' + page.id
          ];

          eventNames.forEach(name => {
            el.dispatchEvent(new CustomEvent(name, eventParams));
          });

          if (el.hasAttribute('data-pass-course')) {
            if (!lastPageSuspendDataSet) {
              lastPageSuspendDataSet = true;
              set('last-page', true);
            }

            const passCourse = el?.getAttribute('data-pass-course');

            if (
              passCourse &&
              (typeof passCourse === 'number' || !isNaN(Number(passCourse)))
            ) {
              completeCourse(Number(passCourse));
            } else if (
              passCourse &&
              typeof passCourse === 'string' &&
              passCourse !== 'true' &&
              passCourse !== 'false'
            ) {
              completeCourse(passCourse);
            } else {
              completeCourse();
            }
          }

          el.querySelectorAll('video[uc-autoplay]').forEach(
            (video: HTMLVideoElement) => {
              if (this.suspend.reducedMotion) {
                video.pause();
                video.currentTime = 0;
                video.classList.add('auto-alpha-1');
              } else {
                video.play();
                video.autoplay = true;
              }
            }
          );

          const playingOutsideSection = Boolean(
            currentAudioOwner() && currentAudioOwner() !== id
          );

          if (playingOutsideSection) {
            stopAudio();
          }

          const dontAutoComplete =
            el.querySelector('.unlock') ||
            el.querySelector('.dont-unlock') ||
            el.matches('.unlock') ||
            el.matches('.dont-unlock');

          const completed = getCompleted(id);

          if (!completed && !dontAutoComplete) {
            gsap.delayedCall(isTall ? 2 : 0, () => {
              setCompleted(id);
              page.refreshSections();
            });
          }

          // start animating in the elements as we scroll the section in to view
          this.fadeInElements(el, false);

          this.logoVisibility(el);

          gsap.delayedCall(0.5, () => {
            this.playAudio(el);
          });
        } else {
          // skip the fade anim as we scroll back up to previously viewed sections
          this.fadeInElements(el, true);
        }
      },
      onLeave: () => {
        el.dispatchEvent(
          new CustomEvent('scrollsectionleave', {
            bubbles: true,
            detail: this.st1
          })
        );
      },
      onLeaveBack: () => {
        el.dispatchEvent(
          new CustomEvent('scrollsectionleaveback', {
            bubbles: true,
            detail: this.st1
          })
        );
      }
    });

    // scrolltrigger to reset the fade-in anims when we scroll back up and the section drops out of view
    this.st2 = ScrollTrigger.create({
      // markers: true,
      trigger: el,
      scroller,
      start: 'top bottom',
      end: 'bottom top',
      onLeaveBack: () => {
        if (!el.offsetHeight) return;

        // as we scroll back up, we need to reset the fade-in anims ready for the next time we scroll into view
        this.resetFadeElements(el);
      }
    });

    page.unlockAndScroll = async (id: string) => {
      setCompleted(id);
      await page.refreshSections();
      await nextTick();
      scroller.scrollOne('next', {
        from: '#' + id,
        force: true,
        ease: 'power1.inOut',
        // delay: 0.15,
        duration: 1
      });
    };

    window.addEventListener('pageenter--' + page.id, async () => {
      await new Promise(r => setTimeout(r, 500));
      this.refresh();
      await this.$nextTick();
      scroller.scrollTo(0, 0);
    });

    window.addEventListener('tagschanged', this.refresh);

    watch(
      () => this.suspend.reducedMotion,
      () => {
        nextTick(() => {
          if (this.st1?.isActive) {
            this.fadeInElements(el, true);
          }
        });
      }
    );

    // setViewed(this.id + `-section0`);

    el.ScrollTrigger = this.st1;
  },
  unmounted() {
    this.st1.kill();
    this.st2.kill();
    this.resetFadeElements(this.$el);
    window.removeEventListener('tagschanged', this.refresh);
  },
  methods: {
    ...mapActions(useQuizzes, ['completeCourse']),

    async refresh() {
      await nextTick();

      this.st1.refresh(true);
      this.st2.refresh(true);
    },

    containsStickyScreen(id) {
      if (!id) return false;

      const section = this.sections.find(
        section => id === section.id
      ) as HTMLElement;
      return section &&
        section.querySelector(
          '.screen.screen--sticky-left, .screen.screen--sticky-right, .screen.screen--quiz-progress'
        )
        ? true
        : false;
    },

    logoVisibility(screen) {
      const logo = document.querySelector('.bottom-left-logo');

      if (!logo) return;

      const hasVideo = screen.querySelector('.screen--video');
      const hasSlides = screen.querySelector('.screen--slides');
      const hasHSG = screen.querySelector('.screen--hotspot-game');
      if (hasVideo || hasSlides || hasHSG) {
        logo.classList.add('hidden');
        logo.classList.remove('xl_block');
      } else {
        logo.classList.add('hidden', 'xl_block');
      }
    },

    playAudio: debounce(
      function (el) {
        const owner = String(this?.id || el?.id || '');

        // dont play screen-level audio on scroll for certain templates
        const hasVideo = el.querySelector('.screen--video');
        const hasSlides = el.querySelector('.screen--slides');
        const hasAssessment = el.querySelector('.screen--assessment');
        const hasBank = el.querySelector('.screen--bank');
        const hasQuiz = el.querySelector('.screen--quiz');
        const hasCourseTitle = el.querySelector('.screen--course-title');
        const hasHSG = el.querySelector('.screen--hotspot-game');
        const hasStickySplit = el.querySelector(
          '.screen--sticky-left, .screen--sticky-right'
        );
        if (
          hasCourseTitle ||
          hasVideo ||
          hasSlides ||
          hasAssessment ||
          hasBank ||
          hasQuiz ||
          hasHSG ||
          hasStickySplit
        )
          return;

        const primaryEl = el.querySelector(
          '[data-audio]'
        ) as HTMLElement | null;
        const audio = primaryEl?.getAttribute('data-audio');
        const elsToQueue = Array.from(
          el.querySelectorAll('[data-queue-audio]') as NodeListOf<HTMLElement>
        );
        const queuedAudio: string[] = [];

        for (const q of elsToQueue) {
          const queueAudio = q.getAttribute('data-queue-audio');
          if (queueAudio) queuedAudio.push(queueAudio);
        }

        if (audio && audio !== '') {
          queuedAudio.forEach(src => queue(src, { owner }));
          play(audio, { delay: 0.5, owner });
        } else if (queuedAudio.length > 0) {
          const [first, ...rest] = queuedAudio;
          if (!first) return;
          rest.forEach(src => queue(src, { owner }));
          play(first, { owner });
        } else if (elsToQueue.length > 0) {
          playNextInQueue();
        }
      },
      300,
      { trailing: true }
    ),
    activate(el: ExtendedElement) {
      el.classList?.add('-in');
      if (el?.play) el.play(); // lotties
    },
    reset(el: ExtendedElement) {
      el.classList?.remove('-in');
      if (el?.reset) el.reset(); // lotties
    },
    fadeInElements(el: HTMLElement, skipAnim: boolean = false) {
      const self = this;
      const triggerElements = el.querySelectorAll(
        this.suspend.reducedMotion
          ? fadeTriggersSelector
          : noFadeTriggersSelector
      );
      const assetElements = el.querySelectorAll(assetTriggersSelector);

      if (
        skipAnim ||
        this.suspend.reducedMotion ||
        this.isEditModeActive ||
        __AUTHORING__
      ) {
        // console.log('__AUTHORING__ skipAnim', __AUTHORING__);
        const combinedElements = Array.from(triggerElements).concat(
          Array.from(assetElements)
        );
        combinedElements?.forEach(el => {
          self.activate(el);
        });
      } else {
        const hiddenElements = Array.from(triggerElements).filter(el =>
          this.isElementHidden(el)
        );

        const filteredElements = Array.from(triggerElements).filter(
          el => !this.isElementHidden(el)
        );

        const combinedElements = Array.from(hiddenElements).concat(
          Array.from(assetElements)
        );

        // immediately apply '-in' class for elements that are inside hidden elements (so we don't have gaps in the fade-in sequence fading hidden elements) or for images/lotties
        combinedElements?.forEach(el => {
          self.activate(el);
        });

        // use a gsap timeline to fade in animations for specific elements in this section
        const timeline: GSAPTimeline = gsap.timeline({ paused: true });
        el.timeline = timeline;

        filteredElements?.forEach(el => {
          timeline.to(
            el,
            {
              onStart() {
                self.activate(el);
              }
            },
            '<0.1'
          );
          timeline.play(0);
        });
      }
    },
    resetFadeElements(el) {
      if (el.timeline) {
        el.timeline.progress(0).kill();
      }
      el.timeline = null;

      const self = this;
      const triggerElements = el.querySelectorAll(noFadeTriggersSelector);
      const assetElements = el.querySelectorAll(assetTriggersSelector);

      const combinedElements = Array.from(triggerElements).concat(
        Array.from(assetElements)
      );

      combinedElements?.forEach(el => {
        self.reset(el);
      });
    },
    isElementHidden(element) {
      let el = element;
      while (el) {
        const cl = el.classList;
        if (
          cl.contains('hidden') ||
          cl.contains('invisible') ||
          cl.contains('sm_hidden') ||
          cl.contains('md_hidden') ||
          cl.contains('lg_hidden') ||
          cl.contains('sm_invisible') ||
          cl.contains('md_invisible') ||
          cl.contains('lg_invisible') ||
          (cl.contains('auto-alpha-0') && !cl.contains('auto-alpha-1')) ||
          (cl.contains('sm_auto-alpha-0') && !cl.contains('sm_auto-alpha-1')) ||
          (cl.contains('md_auto-alpha-0') && !cl.contains('md_auto-alpha-1')) ||
          (cl.contains('lg_auto-alpha-0') && !cl.contains('lg_auto-alpha-1'))
        ) {
          return true;
        }
        el = el.parentElement;
      }
      return false;
    }
  }
});
</script>

<style scoped>
.section {
  height: 0;
  min-height: 0;
  display: none !important;
  width: 100%;

  /* z-index: 100; */
  position: relative;
  top: 200vh;
}

.-editing .section,
.section--unlocked,
.-measure-scroll-height .section {
  height: auto;
  min-height: 0;
  display: flow-root !important;
  top: 0;

  &.section--fullscreen {
    min-height: 100vh;
  }
}

/**
 * hide the overflow on the last unlocked section, to avoid revealing part of the next section
 * we do not want to do this for sections with sticky left/right/quiz-progress as it breaks the sticky
 */
:nth-last-child(1 of .section.section--unlocked):not(
  .section.section--contains-sticky
) {
  overflow: hidden;
}
</style>

<style></style>
